import React, { useEffect, useState } from 'react';
import supabase from '../../supabase';
import { PlusCircle, Trash, Pencil } from 'lucide-react';
import AddIncomePopup from '../PopUpComponents/AddIncomePopup';
import Loader from "../../Common/Loader";
import { useToast } from '../Invoice/Toast';
import './ManualIncome.css';

export default function ManualIncomeForm({ modalState, openModal, closeModal }) {
  const [user, setUser] = useState(null);
  const [incomes, setIncomes] = useState([]);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    description: '',
    total_amount: '',
    income_date: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);

  // Delete Popup States
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [incomeToDelete, setIncomeToDelete] = useState(null);

  const { showError, showSuccess, ToastContainer } = useToast();

  // Helper function to create URL-friendly slug
  const createSlug = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 50);
  };

  // Handle URL-based modal state changes
  useEffect(() => {
    if (!modalState || incomes.length === 0) return;

    const { action, id } = modalState;

    if (action === 'add') {
      resetForm();
    } else if (action === 'edit' && id) {
      loadIncomeForEdit(id);
    } else if (!action) {
      resetForm();
    }
  }, [modalState, incomes]);

  // Load income for editing by ID or slug
  const loadIncomeForEdit = (incomeIdOrSlug) => {
    // First try to find by ID
    let income = incomes.find(i => i.id === incomeIdOrSlug);
    
    // If not found, try matching by name slug
    if (!income) {
      income = incomes.find(i => {
        const expectedSlug = createSlug(i.name);
        return expectedSlug === incomeIdOrSlug;
      });
    }

    if (income) {
      setFormData({
        id: income.id,
        name: income.name,
        description: income.description,
        total_amount: income.total_amount,
        income_date: income.income_date
      });
      setIsEditing(true);
    }
  };

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      if (user) fetchIncomes(user.id);
    })();
  }, []);

  const fetchIncomes = async (userId) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('income')
        .select('*')
        .eq('user_id', userId)
        .eq('e_status', 'm')
        .order('income_date', { ascending: false });

      if (error) {
        console.error('Error fetching incomes:', error);
        showError('Load Error', 'Failed to load income entries');
      } else {
        setIncomes(data || []);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      showError('Unexpected Error', 'An unexpected error occurred while loading incomes');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ id: null, name: '', description: '', total_amount: '', income_date: '' });
    setIsEditing(false);
  };

  const handleAddIncomeSave = async () => {
    if (!user) {
      showError('Authentication Error', 'User not authenticated');
      return;
    }

    if (!formData.name.trim()) {
      showError('Validation Error', 'Income name is required');
      return;
    }

    if (!formData.total_amount || parseFloat(formData.total_amount) <= 0) {
      showError('Validation Error', 'Please enter a valid amount');
      return;
    }

    if (!formData.income_date) {
      showError('Validation Error', 'Income date is required');
      return;
    }

    try {
      let error;

      if (isEditing) {
        const { error: updateError } = await supabase
          .from('income')
          .update({
            name: formData.name.trim(),
            description: formData.description.trim(),
            total_amount: parseFloat(formData.total_amount),
            income_date: formData.income_date
          })
          .eq('id', formData.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('income')
          .insert([{
            user_id: user.id,
            name: formData.name.trim(),
            description: formData.description.trim(),
            total_amount: parseFloat(formData.total_amount),
            income_date: formData.income_date,
            e_status: 'm'
          }]);
        error = insertError;
      }

      if (error) {
        console.error('Error saving income:', error);
        showError('Save Error', error.message || (isEditing ? 'Failed to update income' : 'Failed to add income'));
      } else {
        showSuccess('Success', isEditing ? 'Income updated successfully!' : 'Income added successfully!');
        resetForm();
        if (closeModal) closeModal();
        fetchIncomes(user.id);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      showError('Unexpected Error', 'An unexpected error occurred');
    }
  };

  const handleAddIncomeCancel = () => {
    resetForm();
    if (closeModal) closeModal();
  };

  const handleEdit = (income, e) => {
    e.stopPropagation();
    if (openModal) {
      openModal('edit', {
        id: income.id,
        name: income.name
      });
    }
  };

  const openDeletePopup = (income, e) => {
    e.stopPropagation();
    setIncomeToDelete(income);
    setShowDeletePopup(true);
  };

  const closeDeletePopup = () => {
    setShowDeletePopup(false);
    setIncomeToDelete(null);
  };

  const deleteIncome = async () => {
    if (!incomeToDelete) return;

    const income = incomeToDelete;
    closeDeletePopup();

    try {
      const { error } = await supabase
        .from('income')
        .delete()
        .eq('id', income.id);

      if (error) {
        console.error('Error deleting income:', error);
        showError('Delete Error', error.message || 'Failed to delete income entry');
      } else {
        showSuccess('Success', `Income "${income.name}" deleted successfully`);
        fetchIncomes(user.id);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      showError('Unexpected Error', 'An unexpected error occurred');
    }
  };

  const handleAddIncomeClick = () => {
    resetForm();
    if (openModal) {
      openModal('add');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Determine if popup should show based on modalState
  const showAddIncomePopup = modalState?.action === 'add' || modalState?.action === 'edit';

  if (loading) {
    return (
      <div className="manual-ern-loading">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <ToastContainer />

      <div className="manual-ern-container">
        <div className="manual-ern-header">
          <div className="manual-ern-header-left">
            <h1 className="manual-ern-title">Earnings</h1>
          </div>
          
          <div className="manual-ern-header-right">
            <button 
              className="add-income-btn "
              onClick={handleAddIncomeClick}
            >
              Add Income
              <PlusCircle size={18} className="add-icon" />
            </button>
          </div>
        </div>

        {incomes.length === 0 ? (
          <div className="manual-ern-empty">
            <div className="manual-ern-empty-icon">💰</div>
            <h3 className="manual-ern-empty-title">No income entries yet</h3>
            <p className="manual-ern-empty-description">
              Add your first income entry above to start tracking your earnings.
            </p>
            <button 
              className="manual-ern-create-first-income-btn"
              onClick={handleAddIncomeClick}
            >
              Add Your First Income
            </button>
          </div>
        ) : (
          <div className="manual-ern-content">
            <div className="manual-ern-table-view">
              <div className="manual-ern-table-wrapper">
                <div className="manual-ern-table-header-container">
                  <table className="manual-ern-table-header">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Amount</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                  </table>
                </div>
                
                <div className="manual-ern-table-body-container">
                  <table className="manual-ern-table">
                    <tbody>
                      {incomes.map(income => (
                        <tr className="manual-ern-table-row" key={income.id}>
                          <td className="manual-ern-table-cell manual-ern-source-cell">{income.name}</td>
                          <td className="manual-ern-table-cell manual-ern-description-cell">{income.description || 'N/A'}</td>
                          <td className="manual-ern-table-cell manual-ern-amount-cell">₹{parseFloat(income.total_amount).toFixed(2)}</td>
                          <td className="manual-ern-table-cell manual-ern-date-cell">{formatDate(income.income_date)}</td>
                          <td className="manual-ern-table-cell manual-ern-actions-cell">
                            <button 
                              className="manual-ern-edit-button" 
                              onClick={(e) => handleEdit(income, e)}
                              title="Edit income"
                            >
                              <Pencil size={18} />
                            </button>
                            <button 
                              className="manual-ern-delete-button" 
                              onClick={(e) => openDeletePopup(income, e)}
                              title="Delete income"
                            >
                              <Trash size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="manual-ern-card-view">
              {incomes.map((income) => (
                <div key={income.id} className="manual-ern-card">
                  <div className="manual-ern-card-header">
                    <div className="manual-ern-card-title">
                      <h4>{income.name}</h4>
                      <span className="manual-ern-card-amount">
                        ₹{parseFloat(income.total_amount).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="manual-ern-card-body">
                    <div className="manual-ern-card-field">
                      <span className="manual-ern-card-label">Description:</span>
                      <span className="manual-ern-card-value">{income.description || 'N/A'}</span>
                    </div>
                    <div className="manual-ern-card-field">
                      <span className="manual-ern-card-label">Date:</span>
                      <span className="manual-ern-card-value">{formatDate(income.income_date)}</span>
                    </div>
                  </div>
                  
                  <div className="manual-ern-card-actions">
                    <button
                      className="manual-ern-edit-button"
                      onClick={(e) => handleEdit(income, e)}
                      title="Edit Income"
                    >
                      <Pencil size={16} />
                      <span className="manual-ern-btn-text">Edit</span>
                    </button>
                    
                    <button
                      className="manual-ern-delete-button"
                      onClick={(e) => openDeletePopup(income, e)}
                      title="Delete Income"
                    >
                      <Trash size={16} />
                      <span className="manual-ern-btn-text">Delete</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {showAddIncomePopup && (
          <AddIncomePopup
            formData={formData}
            setFormData={setFormData}
            onSave={handleAddIncomeSave}
            onCancel={handleAddIncomeCancel}
            isEditing={isEditing}
            title={isEditing ? 'Edit Income' : 'Add New Income'}
            saveButtonText={isEditing ? 'Update Income' : 'Add Income'}
          />
        )}

        {showDeletePopup && incomeToDelete && (
          <div className="delete-popup-overlay" onClick={closeDeletePopup}>
            <div className="delete-popup" onClick={e => e.stopPropagation()}>
              <div className="delete-popup-header">
                <div className="delete-popup-icon-wrapper">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                </div>
                <h3 className="delete-popup-title">Delete Income Entry</h3>
              </div>
              
              <div className="delete-popup-content">
                <p className="delete-popup-message">
                  Are you sure you want to delete income entry <strong>{incomeToDelete.name}</strong>?
                </p>
                
                <div className="delete-popup-info">
                  <div className="delete-popup-info-item">
                    <span className="delete-popup-label">Amount:</span>
                    <span className="delete-popup-value">₹{parseFloat(incomeToDelete.total_amount).toFixed(2)}</span>
                  </div>
                  {incomeToDelete.description && (
                    <div className="delete-popup-info-item">
                      <span className="delete-popup-label">Description:</span>
                      <span className="delete-popup-value">{incomeToDelete.description}</span>
                    </div>
                  )}
                  <div className="delete-popup-info-item">
                    <span className="delete-popup-label">Date:</span>
                    <span className="delete-popup-value">{formatDate(incomeToDelete.income_date)}</span>
                  </div>
                </div>

                <div className="delete-popup-warning">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                  <span>This action cannot be undone.</span>
                </div>
              </div>
              
              <div className="delete-popup-actions">
                <button className="delete-popup-btn delete-popup-btn-cancel" onClick={closeDeletePopup}>
                  Cancel
                </button>
                <button className="delete-popup-btn delete-popup-btn-delete" onClick={deleteIncome}>
                  Delete Income
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}